// src/utils/RequireTenant.jsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useUser } from "./userContext";
import { getDevUserOverride } from "./userContext";
import WorkspaceLoading from "../components/WorkspaceLoading";

export default function RequireTenant({ children }: { children: React.ReactNode }) {
  const { loading, tenants, activeTenantId, activeTenant } = useUser();
  const { instance } = useMsal();
  const location = useLocation();

  // Wait until we know whether the user has tenants or an active tenant
  if (loading) return <WorkspaceLoading />;

  // In dev mode, bypass tenant check when impersonating (only if active tenant exists and is active)
  const devOverride = getDevUserOverride();
  const devBypass = devOverride && activeTenantId && activeTenant != null && activeTenant.isActive !== false;

  // If user has 0 tenants, show a nice empty-state page
  if (!devBypass && (!tenants || tenants.length === 0)) {
    // Redirect to root so the URL is clean
    if (location.pathname !== "/") {
      return <Navigate to="/" replace />;
    }
    return (
      <div style={styles.page}>
        <div style={styles.card} role="alert" aria-live="polite">
          <div style={styles.header}>
            <div style={styles.iconWrap} aria-hidden="true">
              {/* Simple building icon */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 20V6a2 2 0 0 1 2-2h6v16"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M10 20V10h8a2 2 0 0 1 2 2v8"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M7 8h2M7 11h2M7 14h2M14 13h2M14 16h2"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div>
              <h1 style={styles.title}>No tenant access</h1>
              <p style={styles.subtitle}>
                Your account is signed in, but it isn’t assigned to any tenants
                yet. Ask an admin to add you, then refresh this page.
              </p>
            </div>
          </div>

          <div style={styles.actions}>
            <button
              type="button"
              style={{ ...styles.button, ...styles.primary }}
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>

            <button
              type="button"
              style={{ ...styles.button, ...styles.ghost }}
              onClick={() => {
                void instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
              }}
            >
              Sign out / switch account
            </button>
          </div>

          <div style={styles.footer}>
            <span style={styles.smallText}>
              If you think this is a mistake, verify you’re logged into the
              correct account.
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Already on selecttenant → do NOT redirect (prevents loop)
  if (location.pathname === "/selecttenant") {
    return children;
  }

  // Needs tenant selection (multi-tenant + none selected)
  if (!devBypass && !activeTenantId && tenants.length > 1) {
    return (
      <Navigate to="/selecttenant" replace state={{ from: location.pathname }} />
    );
  }

  return children;
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "calc(100vh - 64px)", // tweak if you have a header height
    display: "grid",
    placeItems: "center",
    padding: 24,
    background:
      "radial-gradient(1200px 600px at 20% 0%, rgba(59,130,246,0.10), transparent 60%)," +
      "radial-gradient(900px 500px at 100% 10%, rgba(16,185,129,0.10), transparent 55%)",
  },

  card: {
    width: "min(640px, 100%)",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 18,
    boxShadow: "0 18px 50px rgba(0,0,0,0.12)",
    padding: 26,
    backdropFilter: "blur(10px)",
  },

  header: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    marginBottom: 14,
  },

  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    color: "rgba(59,130,246,1)",
    background: "rgba(59,130,246,0.12)",
    flex: "0 0 auto",
    marginTop: 2,
  },

  title: {
    margin: "2px 0 6px 0",
    fontSize: 24,
    fontWeight: 750,
    letterSpacing: "-0.02em",
    color: "rgba(0,0,0,0.88)",
  },

  subtitle: {
    margin: 0,
    fontSize: 14.5,
    lineHeight: 1.5,
    color: "rgba(0,0,0,0.62)",
    maxWidth: 520,
  },

  actions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 6,
  },

  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 650,
    cursor: "pointer",
    userSelect: "none",
  },

  primary: {
    background: "rgba(59,130,246,1)",
    color: "white",
    border: "1px solid rgba(59,130,246,1)",
  },

  ghost: {
    background: "transparent",
    color: "rgba(0,0,0,0.78)",
  },

  footer: {
    marginTop: 18,
    paddingTop: 14,
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },

  smallText: {
    fontSize: 12.5,
    color: "rgba(0,0,0,0.55)",
  },
};
